import React from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export interface ActivityRow { activity: string; description: string }

interface Props {
  idPrefix: string;
  rows: ActivityRow[];
  onChange: (rows: ActivityRow[]) => void;
  disabled?: boolean;
}

/** The activities the client asks for (what is to be done), one row each. */
const ActivitiesEditor: React.FC<Props> = ({ idPrefix, rows, onChange, disabled }) => {
  const set = (i: number, patch: Partial<ActivityRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  if (disabled) {
    return rows.length ? (
      <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
        {rows.map((r, i) => (
          <Box component="li" key={i}>
            <Typography variant="body2" fontWeight={600}>{r.activity}</Typography>
            {r.description ? <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{r.description}</Typography> : null}
          </Box>
        ))}
      </Stack>
    ) : <Typography variant="body2" color="text.secondary">No activities.</Typography>;
  }

  return (
    <Stack spacing={1.5}>
      {rows.map((r, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            id={`${idPrefix}-activity-${i}`}
            label={`Activity ${i + 1}`}
            value={r.activity}
            onChange={(e) => set(i, { activity: e.target.value.slice(0, 200) })}
            sx={{ flex: { sm: '0 0 34%' } }}
            fullWidth
          />
          <TextField
            id={`${idPrefix}-activity-desc-${i}`}
            label="Description"
            value={r.description}
            onChange={(e) => set(i, { description: e.target.value.slice(0, 2000) })}
            multiline
            maxRows={4}
            fullWidth
          />
          <IconButton aria-label={`Remove activity ${i + 1}`} onClick={() => onChange(rows.filter((_r, j) => j !== i))} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Box>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onChange([...rows, { activity: '', description: '' }])}>Add activity</Button>
      </Box>
    </Stack>
  );
};

export default ActivitiesEditor;
